import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import {
  SentinelRecord,
  SentinelResultFile,
} from 'features/sentinels/recordTypes'
import {
  resolveRunState,
  type SentinelRunTiming,
  stalledTransitionAt,
} from 'features/sentinels/runState'
import {
  SENTINEL_ENTRY_TYPE,
  SentinelLoadedEntry,
  SentinelRunStatus,
} from 'features/sentinels/types'
import { useRunStateClock } from 'features/sentinels/useRunStateClock'
import { useMemo } from 'react'
import useSWR from 'swr'

/**
 * Faster than the site-wide list's cadence: this is one entry by id, and it is
 * the view someone watching a run is actually looking at.
 */
const RUNNING_POLL_MS = 5000

const timingOf = (
  rows: SentinelLoadedEntry[] | undefined
): SentinelRunTiming => ({
  status: rows?.[0]?.entry?.data?.summary?.status,
  requestedAt: rows?.[0]?.entry?.data?.summary?.requested_at,
  updatedAt: rows?.[0]?.entry?.updated_at,
})

/**
 * Load one sentinel by entry id. The entry itself only carries identity +
 * status; while a run is in progress the entry is re-polled until the
 * training job flips summary.status, and once done the analysis is
 * downloaded from the entry's result.json via a signed URL and merged with
 * the entry's identity into the full record.
 *
 * A run that outlives every server-side deadline stops being polled — see
 * `resolveRunState`. Nothing here starts or restarts a job: the entry is the
 * only thing read, and the only thing that could be watched after a refresh.
 */
export const useSentinelResult = (entryID: string | undefined) => {
  const { authorizedAxios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { data, error, isLoading, mutate } = useGetRolodexEntriesByLoadOptions<
    SentinelLoadedEntry[]
  >(
    entryID
      ? { ids: [entryID], type: [SENTINEL_ENTRY_TYPE], limit: 1 }
      : undefined,
    {
      refreshInterval: (latest) =>
        resolveRunState(timingOf(latest)) === 'running' ? RUNNING_POLL_MS : 0,
      refreshWhenHidden: false,
    }
  )

  const timing = timingOf(data)
  const now = useRunStateClock(stalledTransitionAt(timing))
  const runState = resolveRunState(timing, now)

  const entry = data?.[0]?.entry
  const status: SentinelRunStatus = entry
    ? (entry.data.summary?.status ?? 'done')
    : 'done'
  const resultPath = status === 'done' ? entry?.data.files?.result : undefined

  const {
    data: resultFile,
    error: fileError,
    isLoading: fileLoading,
  } = useSWR(
    entryID && resultPath ? ['sentinel-result', entryID] : null,
    async () => {
      const res = await authorizedAxios.get(
        `${ROLODEX_URL}/api/v2/files/${entryID}/${resultPath}`
      )
      const download = await fetch(res.data as string)
      if (!download.ok) {
        throw new Error(`result file download failed (HTTP ${download.status})`)
      }
      return (await download.json()) as SentinelResultFile
    },
    { revalidateOnFocus: false }
  )

  const record = useMemo<SentinelRecord | undefined>(() => {
    if (!entry || !resultFile) return undefined
    return {
      name: entry.data.name,
      run_id: entry.data.run_id,
      instance_id: entry.tags.instance,
      organization_id: entry.tags.organization,
      site_id: entry.tags.site ?? '',
      created_at: entry.updated_at,
      updated_at: entry.updated_at,
      ...resultFile,
    }
  }, [entry, resultFile])

  return {
    /**
     * The raw entry. Exposed because the durable design view needs the parts
     * that are not the analysis: which context bundle the run was planned from,
     * and which files the job has published so far.
     */
    entry,
    record,
    status,
    runState,
    progress: entry?.data.summary?.progress,
    statusError: entry?.data.summary?.error,
    requestedAt: entry?.data.summary?.requested_at,
    error: error ?? fileError,
    isLoading: isLoading || (!!resultPath && fileLoading),
    mutate,
  }
}
